import { useEffect, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Activity, Clock, CheckCircle, ListTodo } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend,
} from 'recharts';
import { CHART_SERIES, CHART_AXIS, CHART_GRID, CHART_TOOLTIP, CHART_TOOLTIP_ITEM } from '../../../lib/chartColors';
import { useProjectStore } from '../../../store/projectStore';
import { useTaskStore } from '../../../store/taskStore';
import { useTimesheetStore } from '../../../store/timesheetStore';

export function WorkReports() {
    const { projects, fetchProjects } = useProjectStore();
    const { tasks, fetchTasks } = useTaskStore();
    const { timesheets, fetchTimesheets } = useTimesheetStore();

    useEffect(() => {
        fetchProjects();
        fetchTasks();
        fetchTimesheets();
    }, []);

    const kpis = useMemo(() => {
        const totalProjects = projects.length;
        const activeProjects = projects.filter((p: any) => p.status === 'active').length;
        const totalTasks = tasks.length;
        const completedTasks = tasks.filter(t => t.status === 'done').length;
        const totalHours = timesheets.reduce((acc, t) => acc + Number(t.hours || 0), 0);
        const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
        return { totalProjects, activeProjects, totalTasks, completedTasks, totalHours, completionRate };
    }, [projects, tasks, timesheets]);

    const tasksByStatus = useMemo(() => {
        const map: Record<string, number> = {};
        tasks.forEach(t => { map[t.status] = (map[t.status] || 0) + 1; });
        return Object.entries(map).map(([status, count]) => ({ status, count }));
    }, [tasks]);

    const hoursByProject = useMemo(() => {
        const map: Record<string, number> = {};
        timesheets.forEach(t => {
            const name = t.project_name || 'Unassigned';
            map[name] = (map[name] || 0) + Number(t.hours || 0);
        });
        return Object.entries(map).map(([name, value]) => ({ name, value }));
    }, [timesheets]);

    const kpiCards = [
        { label: 'Active Projects', value: `${kpis.activeProjects}/${kpis.totalProjects}`, icon: Activity, tone: 'text-primary bg-primary/10' },
        { label: 'Total Tasks', value: kpis.totalTasks, icon: ListTodo, tone: 'text-info bg-info/10' },
        { label: 'Completion Rate', value: `${kpis.completionRate}%`, icon: CheckCircle, tone: 'text-success bg-success/10' },
        { label: 'Hours Logged', value: `${kpis.totalHours.toFixed(1)}h`, icon: Clock, tone: 'text-warning bg-warning/10' },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="tracking-tight text-foreground font-display text-page-title">Work Reports</h1>
                <p className="text-muted-foreground">Live analytics across projects, tasks, and logged time.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {kpiCards.map(k => (
                    <Card key={k.label}>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">{k.label}</p>
                                <h3 className="text-foreground mt-1 font-display text-metric">{k.value}</h3>
                            </div>
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.tone}`}>
                                <k.icon className="w-4 h-4" />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="flex flex-col">
                    <CardHeader className="border-b border-border">
                        <h3 className="text-foreground font-display text-section-title">Tasks by Status</h3>
                    </CardHeader>
                    <CardContent className="p-6 h-[320px]">
                        {tasksByStatus.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No task data yet.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={tasksByStatus}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                                    <XAxis dataKey="status" stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} />
                                    <YAxis stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                                    <Tooltip contentStyle={CHART_TOOLTIP} itemStyle={CHART_TOOLTIP_ITEM} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                        {tasksByStatus.map((_, i) => <Cell key={i} fill={CHART_SERIES[i % CHART_SERIES.length]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                <Card className="flex flex-col">
                    <CardHeader className="border-b border-border">
                        <h3 className="text-foreground font-display text-section-title">Hours by Project</h3>
                    </CardHeader>
                    <CardContent className="p-6 h-[320px]">
                        {hoursByProject.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No time logged yet.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={hoursByProject} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4}>
                                        {hoursByProject.map((_, i) => <Cell key={i} fill={CHART_SERIES[i % CHART_SERIES.length]} />)}
                                    </Pie>
                                    <Tooltip contentStyle={CHART_TOOLTIP} itemStyle={CHART_TOOLTIP_ITEM} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader className="border-b border-border">
                    <h3 className="text-foreground font-display text-section-title">Project Breakdown</h3>
                </CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-secondary/50 text-muted-foreground font-semibold border-b border-border">
                            <tr>
                                <th className="px-6 py-3">Project</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3 text-right">Tasks</th>
                                <th className="px-6 py-3 text-right">Hours Logged</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {projects.length === 0 ? (
                                <tr><td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">No projects yet.</td></tr>
                            ) : projects.map((p: any) => {
                                const taskCount = tasks.filter(t => t.projectId === p.id).length;
                                const hours = timesheets.filter(t => t.project_id === p.id).reduce((a, t) => a + Number(t.hours || 0), 0);
                                return (
                                    <tr key={p.id} className="hover:bg-secondary/20">
                                        <td className="px-6 py-4 font-medium text-foreground">{p.name}</td>
                                        <td className="px-6 py-4 capitalize">{p.status}</td>
                                        <td className="px-6 py-4 text-right">{taskCount}</td>
                                        <td className="px-6 py-4 text-right">{hours.toFixed(1)}h</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}
